/**
 * Type definitions for SDK v2
 */

// ============================================================================
// Locale Types
// ============================================================================

export type Locale = 'en' | 'de' | 'es' | 'fr' | 'it' | 'nl' | 'pl' | 'pt';

// ============================================================================
// Style Types
// ============================================================================

export type FontFamily =
  | 'DM Sans'
  | 'Inter'
  | 'Poppins'
  | 'Nunito'
  | 'Work Sans'
  | 'Manrope'
  | 'Rubik'
  | 'Karla'
  | 'Figtree'
  | 'Outfit'
  | 'Space Grotesk'
  | 'Urbanist';

export type FontWeight =
  | 100
  | 200
  | 300
  | 400
  | 500
  | 600
  | 700
  | 800
  | 900
  | 'normal'
  | 'bold'
  | 'lighter'
  | 'bolder';

export type Opacity = number | string; 

export interface InputContainerStyles {
  backgroundColor?: string;
  borderColor?: string;
  borderEnabled?: boolean;
  borderRadius?: string;
}

export interface InputStyles {
  backgroundColor?: string;
  borderColor?: string;
  borderEnabled?: boolean;
  borderRadius?: string;
  textColor?: string;
  placeholderColor?: string;
}

export interface ButtonBaseStyles {
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: string;
  borderColor?: string;
  borderEnabled?: boolean;
  fontSize?: string;
  fontWeight?: FontWeight;
  opacity?: Opacity;
}

export interface ButtonStyles extends ButtonBaseStyles {
  hover?: ButtonBaseStyles;
  disabled?: ButtonBaseStyles;
  loaderColor?: string;
}

export interface GeneralMessageStyles {
  textColor?: string;
  backgroundColor?: string;
  borderEnabled?: boolean;
  borderRadius?: string;
  borderColor?: string;
}

export interface ThemeStyles {
  inputContainer?: InputContainerStyles;
  input?: InputStyles;
  button?: ButtonStyles;
  disclaimerColor?: string;
  fieldErrorColor?: string;
  generalError?: GeneralMessageStyles;
  generalSuccess?: GeneralMessageStyles;
}

export interface CSSProperties {
  fontFamily?: FontFamily;
  fillParent?: boolean;
  inputContainer?: InputContainerStyles;
  input?: InputStyles;
  button?: ButtonStyles;
  disclaimerColor?: string;
  fieldErrorColor?: string;
  generalError?: GeneralMessageStyles;
  generalSuccess?: GeneralMessageStyles;
  // Theme-specific overrides
  dark?: ThemeStyles;
}

// ============================================================================
// SDK Config Types
// ============================================================================

export interface SDKConfig {
  /** Public API key */
  publicKey?: string;
  /** Payment configuration */
  config?: PaymentConfig;
  /** Container element or selector where to mount the iframe (if not provided, creates a popup) */
  container?: string | HTMLElement;
  /** Locale for the UI. Defaults to 'en' */
  locale?: Locale;
  /** Callback when payment succeeds */
  onSuccess?: (data: TransactionData) => void;
  /** Callback when payment fails */
  onError?: (error: any) => void;
  /** Callback when user closes the payment modal */
  onClose?: () => void;
  /** Show default success UI after payment (default true). If false, only unmount iframe. */
  showDefaultSuccessUI?: boolean;
}

export interface PaymentConfig {
  name?: string;
  amount?: number;
  currency?: string;
  paymentId?: string;
  /** Custom styling for the card element */
  style?: CSSProperties;
  /** Custom button text (default: "Complete Payment") */
  buttonText?: string;
  /** Custom placeholder text for inputs */
  placeholders?: {
    cardNumber?: string;
    expiry?: string;
    cvc?: string;
  };
  /** Show default success UI after payment (default true). If false, only unmount iframe. */
  showDefaultSuccessUI?: boolean;
  [key: string]: any;
}

export interface IframeMessage {
  type: 'sdkData' | 'success' | 'error' | 'close' | '3ds-required' | 'iframe-ready' | 'content-height' | '3ds-failed' | '3ds-success';
  data?: any;
  config?: PaymentConfig;
  threeDsSessionUrl?: string; // URL for 3DS challenge when type is '3ds-required'
  paymentId?: string; // Payment ID for 3DS flow
  sessionId?: string; // 3DS session ID when type is '3ds-completed'
  success?: boolean; // 3DS result when type is '3ds-result'
  height?: number; // Content height when type is 'content-height'
}

export interface TransactionData {
  transaction?: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    created_at: string;
    [key: string]: any;
  };
  [key: string]: any;
}

// ============================================================================
// Payment Result Types
// ============================================================================

export enum PaymentResultStatus {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export enum PaymentResultErrorCode {
  THREE_DS_FAILED = 'THREE_DS_FAILED',
  PAYMENT_PROCESSING_ERROR = 'PAYMENT_PROCESSING_ERROR',
}

export interface PaymentError {
  code: PaymentResultErrorCode;
  message: string;
  retryable: boolean;
}

export interface PaymentResult {
  status: PaymentResultStatus;
  paymentId: string;
  error?: PaymentError;
}

export interface CardElementState {
  complete: boolean;
}

